// ============================================================
// TalentsCard - "Active Talents" for both Overview sheets:
// the character's own talents (reroll-hint talents highlighted)
// followed by a collapsible "Granted by <trait> (N)" group for
// trait-granted talents (e.g. the Astartes package). Narrative
// talents carry a (Narrative) tag.
// ============================================================
import { memo, useMemo, useState } from 'react';
import sheet from '../../styles/ActionSheet.module.css';
import styles from '../../styles/OverviewSheet.module.css';
import { getTalent } from '../../engine/character/talentCatalog';
import { getTrait, TRAIT_CATALOG } from '../../engine/character/traitCatalog';
import { getGrantedTalents } from '../../engine/character/effectiveTalents';
import { talentText, type InstanceText } from '../../engine/character/instanceText';
import type { Character, TalentInstance } from '../../engine/character/types';

const REROLL_HINT_IDS = new Set(['hunter_of_aliens', 'scourge_of_heretics']);

/** The small tag beside a talent with no rules behind it. */
function tagOf(inst: TalentInstance, text: InstanceText): string | null {
  if (text.custom) return 'Custom';
  return getTalent(inst.talentId)?.category === 'narrative' ? 'Narrative' : null;
}

function TalentEntry({ inst }: { inst: TalentInstance }) {
  const text = talentText(inst);
  if (!text) return null;
  const isRerollHint = REROLL_HINT_IDS.has(inst.talentId);
  return (
    <div className={isRerollHint ? `${sheet.textListEntry} ${sheet.textListEntryHighlight}` : sheet.textListEntry}>
      <span className={isRerollHint ? sheet.textListNameHighlight : sheet.textListName}>{text.name}</span>
      {inst.parameter && <span className={sheet.textListParam}>({inst.parameter})</span>}
      {tagOf(inst, text) && <span className={styles.narrativeTag}>{tagOf(inst, text)}</span>}
      <div className={sheet.textListDesc}>{text.description}</div>
    </div>
  );
}

/** Trait-granted talents, rolled up behind "Granted by <trait> (N)". */
interface GrantedProps {
  character: Character;
  granted: TalentInstance[];
  /** Held by the card, so the group remembers being open while it has nothing to show. */
  open: boolean;
  onToggle: () => void;
}

function GrantedGroup({ character, granted, open, onToggle }: GrantedProps) {
  const grantingTraits = useMemo(
    () =>
      (character.traits ?? [])
        .filter((t) => TRAIT_CATALOG[t.traitId]?.grants?.length)
        .map((t) => getTrait(t.traitId)?.name || t.traitId),
    [character],
  );
  return (
    <div className={styles.grantedGroup}>
      <button type="button" className={styles.grantedToggle} aria-expanded={open} onClick={onToggle}>
        {open ? '▼' : '▶'} Granted by {grantingTraits.join(', ') || 'traits'} ({granted.length})
      </button>
      {open && granted.map((t) => <TalentEntry key={'granted-' + t.talentId + (t.parameter || '')} inst={t} />)}
    </div>
  );
}

function TalentsCard({ character }: { character: Character }) {
  const [grantedOpen, setGrantedOpen] = useState(false);
  const granted = useMemo(() => getGrantedTalents(character), [character]);
  const ownTalents = character.talents ?? [];
  if (ownTalents.length === 0 && granted.length === 0) return null;

  return (
    <div className={styles.card} data-testid="talents-card">
      <div className={styles.cardTitle}>Active Talents</div>
      <div className={styles.scrollList}>
        {ownTalents.map((t, i) => (
          // By index: one catalog talent may be taken more than once.
          <TalentEntry key={`${i}-${t.talentId}`} inst={t} />
        ))}
        {granted.length > 0 && (
          <GrantedGroup
            character={character}
            granted={granted}
            open={grantedOpen}
            onToggle={() => setGrantedOpen((o) => !o)}
          />
        )}
      </div>
    </div>
  );
}

export default memo(TalentsCard);
